import { hasBaselinesBelow, isSplitParent } from "../ui/render/baseline_helper";
import isHeadless from "../../utils/is_headless";

export default function (gantt) {
  gantt.config.baselines = {
    datastore: "baselines",
    render_mode: false,
    dataprocessor_baselines: false,
    row_height: 16,
    bar_height: 8
  };

  function initBaselineFields(item, task) {
    // don't add baseline if it doesn't belong to any task
    // or if doesn't have the dates
    if (!item.task_id || (!item.start_date && !item.end_date)) {
      return false;
    }

    if (item.start_date) {
      item.start_date = gantt.date.parseDate(item.start_date, "parse_date");
    } else {
      item.start_date = null;
    }

    if (item.end_date) {
      item.end_date = gantt.date.parseDate(item.end_date, "parse_date");
    } else {
      item.end_date = null;
    }

    // set min baseline duration
    item.duration = item.duration || 1;

    // GS-2636. Calculate the 3rd date parameter basing on existing date parameters
    if (item.start_date && !item.end_date){
      item.end_date = gantt.calculateEndDate(item.start_date, item.duration);
    } else if (item.end_date && !item.start_date){
      item.start_date = gantt.calculateEndDate(item.end_date, -item.duration);
    } 
  }
  // gantt init
  const baselineStore = gantt.createDatastore({
    name: gantt.config.baselines.datastore,
    initItem: function (item) {
      if (!item.id) {
        item.id = gantt.uid();
      }

      initBaselineFields(item);

      return item;
    }
  });

  gantt.$data.baselineStore = baselineStore;

  // GS-3535. baseline id -> id of the task the baseline is currently linked to.
  // Kept next to the items instead of on them on purpose: the datastore items are read in bulk
  // on every repaint and on every task update, and a bookkeeping field on them shows up in
  // those loops. A plain object is used so the keys coerce exactly like the datastore's own pull.
  // The recorded owner is what makes a re-assignment detectable - task_id alone cannot be
  // trusted, because the caller may have changed it in place before the datastore reports it
  let linkedTasks = {};

  function _syncBaselines(task) {
    let shouldRepaint = false;
    const iteratedBaselines = {};

    const taskBaselines = task.baselines || [];
    const existingBaselines = gantt.getTaskBaselines(task.id);

    if (taskBaselines.length != existingBaselines.length) {
      shouldRepaint = true;
    }

    taskBaselines.forEach(function (baseline) {
      iteratedBaselines[baseline.id] = true;
      const exisingBaseline = baselineStore.getItem(baseline.id);
      if (exisingBaseline) {
        const start = +exisingBaseline.start_date !== +baseline.start_date;
        const end = +exisingBaseline.end_date !== +baseline.end_date;
        if (start || end) {
          baselineStore.updateItem(baseline.id, baseline);
        } else if (exisingBaseline !== baseline) {
          // GS-3535. task.baselines was given another object for an id the datastore already
          // holds. There is nothing to update value-wise, but the two collections must not be
          // left on different objects - the next datastore update would only reach one of them
          _addBaselinesToTask(exisingBaseline);
        }
      } else {
        baselineStore.addItem(baseline);
      }
    });

    existingBaselines.forEach(function (baseline) {
      if (!iteratedBaselines[baseline.id]) {
        baselineStore.removeItem(baseline.id);
      }
    });

    if (shouldRepaint) {
      if (isSplitParent(task)){
        // here we adjust the parent height, then adjust it with the total child height
        _adjustSplitParentHeight (task);
      } else {
        gantt.adjustTaskHeightForBaselines(task);
      }
      gantt.render();
    }
  }

  function _deleteOrphanBaselines() {
    baselineStore.eachItem(function (baseline) {
      if (!gantt.isTaskExists(baseline.task_id)) {
        baselineStore.removeItem(baseline.id);
      }
    });
  }

  function _adjustSplitParentHeight (task){
    let maxParentHeight = 0;

    gantt.adjustTaskHeightForBaselines(task);

    gantt.eachTask(function(child){
      let childHeight = child.row_height || gantt.config.row_height;

      maxParentHeight = maxParentHeight || childHeight;
      if (childHeight > maxParentHeight){
        maxParentHeight = childHeight;
      }
    }, task.id);

    if (task.row_height < maxParentHeight){
      task.row_height = maxParentHeight;
    }
  }

  gantt.adjustTaskHeightForBaselines = function (task) {
    let height, baselineSize, betweenBaselines;
    let margins = 2;
    let baselineAmount = task.baselines && task.baselines.length || 0;
    const subrowHeight = gantt.config.baselines.row_height;
    //const baselineBarHeight = task?.baselines.bar_height

    const timelineView = gantt.getLayoutView("timeline");
    if (!timelineView || !gantt.config.show_chart){
      return;
    }

    switch (gantt.config.baselines.render_mode) {
      case "taskRow":
        // GS-3535. Without a bar_height the sum is NaN, which every reader of row_height
        // silently falls back to gantt.config.row_height. Leaving the property alone gives
        // the very same layout without writing a broken value into the task
        if (task.bar_height) {
          task.row_height = task.bar_height + 8;
        }
        break;

      case "separateRow":
        height = timelineView.getBarHeight(task.id);
        if (baselineAmount) {
          task.bar_height = task.bar_height || height;

          if (task.bar_height > height){
            height = task.bar_height;
          }

          task.row_height = height + subrowHeight;
        } else if (task.bar_height) {
          task.row_height = task.bar_height + 4;
        }

        _increaseSplitParentHeight(task);
        break;

      case "individualRow":
        height = timelineView.getBarHeight(task.id);

        if (baselineAmount) {
          task.bar_height = task.bar_height || height;

          if (task.bar_height > height){
            height = task.bar_height;
          }

          baselineSize = subrowHeight * baselineAmount;
       //   betweenBaselines = 3 * (baselineAmount + 1);

          task.row_height = height + baselineSize + margins;
        } else if (task.bar_height){
          task.row_height = task.bar_height + 4;
        }

        _increaseSplitParentHeight(task);
        break;
      default:
        // GS-3535. Same NaN guard as in the "taskRow" branch above
        if (task.bar_height) {
          task.row_height = task.bar_height + 8;
        }
        break;
    }
  };

  function _increaseSplitParentHeight(task) {
    gantt.eachParent(function (parent) {
      if (isSplitParent(parent)) {
        const parentHeight = parent.row_height || gantt.getLayoutView("timeline").getBarHeight(parent.id);
        let maxHeight = task.row_height;

        // iterate only direct children
        const subtasks = gantt.getChildren(parent.id);
        subtasks.forEach(function (subtaskId) {
          const subtask = gantt.getTask(subtaskId);
          if (subtask.id == task.id) {
            return;
          }
          const subtaskHeight = subtask.row_height || gantt.getLayoutView("timeline").getBarHeight(subtask.id);
          maxHeight = maxHeight || subtaskHeight;
          if (subtaskHeight > maxHeight) {
            maxHeight = subtaskHeight;
          }
        });

        parent.row_height = maxHeight;
        parent.bar_height = parent.bar_height || parentHeight;
      }
    }, task.id);
  }

  function _adjustTaskHeight(task){
    if(isHeadless(gantt)){
      return;
    }
    if (isSplitParent(task)){
      _adjustSplitParentHeight (task);
    } else {
      gantt.adjustTaskHeightForBaselines(task);
    }
  }

  // GS-3535. Index of a baseline id inside a task.baselines array, -1 when it is not there
  function _findBaselineIndex(taskBaselines, baselineId){
    if (!taskBaselines) {
      return -1;
    }
    for (let i = 0; i < taskBaselines.length; i++) {
      if (taskBaselines[i].id == baselineId) {
        return i;
      }
    }
    return -1;
  }

  // GS-3535. task.baselines holds the datastore items themselves rather than copies of them,
  // so the two collections cannot drift apart. Idempotent: it is the single place that decides
  // whether a datastore item is already mirrored on its task, and every mutation hook routes
  // through it rather than pre-judging that on its own
  function _addBaselinesToTask(baseline){
    if (!baseline) {
      return;
    }

    const taskId = baseline.task_id;
    if (!gantt.isTaskExists(taskId)) {
      // the baseline is not renderable, but it must not stay linked to its previous task either
      _removeBaselineFromTask(baseline);
      return;
    }

    const task = gantt.getTask(taskId);
    const linkedTaskId = linkedTasks[baseline.id];
    const index = _findBaselineIndex(task.baselines, baseline.id);

    // Already mirrored: the recorded owner still matches AND the task holds this very object.
    // Both halves are needed - ownership alone does not prove the two collections still share
    // one object, because addItem() of a known id replaces the object the datastore stores,
    // and task.baselines is a public field an application may reassign. A plain value update
    // ends here: the set of baselines did not change, so neither can the row height
    if (linkedTaskId == taskId && index !== -1 && task.baselines[index] === baseline) {
      return;
    }

    if (linkedTaskId !== undefined && linkedTaskId != taskId) {
      // a re-assignment: this splices the baseline out of another task, so the index found
      // above stays valid
      _removeBaselineFromTask(baseline);
    }

    task.baselines = task.baselines || [];
    if (index === -1) {
      task.baselines.push(baseline);
    } else {
      // a repeated parse or add of the same id puts a brand new object into the datastore
      task.baselines[index] = baseline;
    }

    linkedTasks[baseline.id] = taskId;
    _adjustTaskHeight(task);
  }

  // GS-3535. Counterpart of _addBaselinesToTask, drops the link recorded in linkedTasks
  function _removeBaselineFromTask(baseline){
    if (!baseline) {
      return;
    }

    const linkedTaskId = linkedTasks[baseline.id];
    delete linkedTasks[baseline.id];

    if (linkedTaskId === undefined || !gantt.isTaskExists(linkedTaskId)) {
      return;
    }

    const task = gantt.getTask(linkedTaskId);
    const index = _findBaselineIndex(task.baselines, baseline.id);
    if (index === -1) {
      return;
    }

    task.baselines.splice(index, 1);
    _adjustTaskHeight(task);
  }

  // GS-3535. Adding, parsing or updating an id that is already in the datastore can re-assign
  // the baseline to another task. Called before the datastore applies the change, while the
  // previous owner is still the one recorded in linkedTasks
  function _unlinkReassignedBaseline(baselineId, incomingBaseline){
    if (baselineId === undefined || baselineId === null) {
      return;
    }

    const linkedTaskId = linkedTasks[baselineId];
    if (linkedTaskId === undefined) {
      return;
    }

    const storedBaseline = baselineStore.getItem(baselineId);
    if (!storedBaseline) {
      return;
    }

    // the incoming object may be a partial update that does not mention the task at all
    const nextTaskId = incomingBaseline && incomingBaseline.task_id !== undefined
      ? incomingBaseline.task_id
      : storedBaseline.task_id;

    if (linkedTaskId != nextTaskId) {
      _removeBaselineFromTask(storedBaseline);
    }
  }

  gantt.attachEvent(
    "onGanttReady",
    function () {

      if(!gantt.config.baselines){
        return;
      }

      gantt.attachEvent("onParse", function () {
        baselineStore.eachItem(function (baseline) {
          _addBaselinesToTask(baseline);
        });
      });

      gantt.attachEvent("onBeforeTaskUpdate", function (id, task) {
        _syncBaselines(task);
        return true;
      });

      gantt.attachEvent("onAfterUndo", function(action){
        const baselinesRenderedBelow = gantt.config.baselines.render_mode == "separateRow" || gantt.config.baselines.render_mode == "individualRow";
        if (baselinesRenderedBelow && action){
          let repaint = false;
          action.commands.forEach(function(command){
            if (command.entity == "task"){
              const taskId = command.value.id;
              if (gantt.isTaskExists(taskId)){
                const task = gantt.getTask(taskId);
                if (task.parent && gantt.isTaskExists(task.parent)){
                  const parent = gantt.getTask(task.parent);
                  if (isSplitParent(parent)){
                    _adjustSplitParentHeight (parent);
                    repaint = true;
                  }
                }
              }    
            }
          });
          if (repaint){
            gantt.render();
          }
        }
      });

      gantt.attachEvent("onAfterTaskDelete", function (id, task) {
        if (hasBaselinesBelow){
          if (task.parent && gantt.isTaskExists(task.parent)){
            const parent = gantt.getTask(task.parent);
            if (isSplitParent(parent)){
              _adjustSplitParentHeight (parent);
            }
          }
        }
        _deleteOrphanBaselines();
      });

      gantt.getTaskBaselines = function (taskId) {
        const baselines = [];
        baselineStore.eachItem(function (baseline) {
          if (baseline.task_id == taskId) {
            baselines.push(baseline);
          }
        });
        return baselines;
      };
      gantt.$data.baselineStore.attachEvent("onClearAll", function () {
        // GS-3535: nothing is linked any more
        linkedTasks = {};

        gantt.eachTask(function(task){
          if (task.baselines){
            delete task.baselines;
            // GS-3535: a row enlarged to fit the baselines has to shrink back
            _adjustTaskHeight(task);
          }
        });
        // The data should not be repainted as otherwise it causes issues when we have resource 
        // assignments and group tasks. It should be fixed when we add a repaint stack
        // gantt.refreshData();
        return true;
      });
      // GS-3535: every datastore mutation is mirrored on task.baselines through the same two
      // hooks - a "before" one that drops the link recorded on the currently stored item, and
      // an "after" one that links whatever object the datastore ended up holding. Reading the
      // stored item before the change and linking after it is what makes the in-place and
      // partial forms of the datastore API work.
      // GS-3170: adding with the datastore
      gantt.$data.baselineStore.attachEvent("onBeforeAdd", function(id, item){
        _unlinkReassignedBaseline(id, item);
      });
      gantt.$data.baselineStore.attachEvent("onAfterAdd", function(id){
        // getItem(id) rather than the object passed in: adding a known id replaces the stored
        // object, and it is the stored one the task has to end up sharing.
        // _addBaselinesToTask returns right away when the two are already the same object
        _addBaselinesToTask(baselineStore.getItem(id));
      });
      // the datastore's own parse() bypasses onBeforeAdd and the gantt-level onParse, so
      // without these handlers the baselines would stay unlinked and no bars would be rendered
      gantt.$data.baselineStore.attachEvent("onBeforeParse", function(rawBaselines){
        rawBaselines.forEach(function(baseline){
          _unlinkReassignedBaseline(baseline.id, baseline);
        });
        return true;
      });
      gantt.$data.baselineStore.attachEvent("onParse", function(loadedBaselines){
        loadedBaselines.forEach(function(baseline){
          _addBaselinesToTask(baseline);
        });
      });
      gantt.$data.baselineStore.attachEvent("onAfterDelete", function(id, baseline){
        _removeBaselineFromTask(baseline);
      });
      // updateItem() can move a baseline to another task, and it accepts both a full item and
      // a partial object, so the id argument of the event is the only reliable key here
      gantt.$data.baselineStore.attachEvent("onBeforeUpdate", function(id, item){
        _unlinkReassignedBaseline(id, item);
        return true;
      });
      gantt.$data.baselineStore.attachEvent("onAfterUpdate", function(id){
        // onBeforeUpdate has already unlinked the item if the update moved it to another task.
        // A plain value update lands on the object the task already shares, so the call below
        // returns without touching the task
        _addBaselinesToTask(baselineStore.getItem(id));
      });
      gantt.$data.tasksStore.attachEvent("onClearAll", function () {
        baselineStore.clearAll();
        return true;
      });
      gantt.attachEvent("onTaskIdChange", function (id, new_id) {
        const baselines = baselineStore.find(function (a) {
          return a.task_id == id;
        });
        baselines.forEach(function (a) {
          a.task_id = new_id;
          baselineStore.updateItem(a.id);
        });
      });
    },
    { once: true }
  );
};
